/**
 * PlatformsContext — which platforms the signed-in person administers, from each API's
 * GET v3/{platform}/admin/capabilities (docs/architecture/admin-portal-shape.md §5).
 * The header switcher, the landing page and the perimeter gate read it.
 *
 *   Retail — pcb-retail-api v3/retail/admin/capabilities. Until that route is deployed (404)
 *            or when it cannot be reached, Retail falls back to the ROP roles the panel
 *            already resolved (the RoleGate list), so nobody loses access during the rollout.
 *   Hub    — pcb-innovation-api v3/hub/admin/capabilities, called only with the shared
 *            backend token; without it the Hub is 'consent-waiting'.
 *   Loans  — pcb-loans-api v3/loans/admin/capabilities (src/platforms/loans, LOP-owned), same
 *            shared backend token as the Hub; a 403 means not a loans admin.
 *   Finance — no module yet: 'unavailable'.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ApiError } from '@retail-ops/api-client';
import { useAuth } from '@/contexts/AuthContext';
import { useApiClient } from '@/contexts/ApiClientContext';
import { onBackendTokenState } from '@/auth/tokens';
import { ConsentWaitingError, hubApi } from '@/platforms/hub/hubApi';
import { LoansApiError, loansApi } from '@/platforms/loans/loansApi';
import { RETAIL_PAGES } from '@/platforms/retail/retailPages';
import { SIX_SECTIONS, type Capabilities, type PlatformId, type PlatformStatus } from '@/platforms/types';

/** The roles the panel has always admitted (components/RoleGate.tsx). */
export const RETAIL_ADMIN_ROLES = ['SuperAdmin', 'Admin', 'RetailAdmin', 'SysOpsManager', 'RiskManager', 'DisclosureManager'] as const;

export interface PlatformDef {
  id: PlatformId;
  label: string;
  path: string;
}

export const PLATFORMS: ReadonlyArray<PlatformDef> = [
  { id: 'retail', label: 'Retail', path: '/retail' },
  { id: 'loans', label: 'Loans', path: '/loans' },
  { id: 'hub', label: 'Innovation Hub', path: '/hub' },
  { id: 'finance', label: 'Finance', path: '/finance' },
];

type StatusMap = Record<PlatformId, PlatformStatus>;

interface PlatformsValue {
  status: StatusMap;
  /** Platforms the person administers (status ready and isAdmin). */
  available: ReadonlyArray<PlatformDef>;
  /** True while any platform's capabilities are still loading. */
  loading: boolean;
  refresh: () => void;
}

const PlatformsContext = createContext<PlatformsValue | null>(null);

const INITIAL: StatusMap = {
  retail: { kind: 'loading' },
  hub: { kind: 'loading' },
  loans: { kind: 'loading' },
  finance: { kind: 'unavailable' },
};

function hasRetailRole(roles: ReadonlyArray<string>): boolean {
  return roles.some((r) => (RETAIL_ADMIN_ROLES as ReadonlyArray<string>).includes(r));
}

/** Capabilities derived from ROP roles, for when the API route is not there yet. */
function retailFromRoles(roles: ReadonlyArray<string>): Capabilities {
  const pages = RETAIL_PAGES.filter((pg) => pg.roles.some((r) => roles.includes(r)));
  const used = new Set(pages.map((pg) => pg.section));
  return {
    platform: 'retail',
    displayName: 'Retail Operations',
    isAdmin: hasRetailRole(roles),
    sections: SIX_SECTIONS.filter((s) => used.has(s.id)).map((s) => ({ id: s.id, label: s.label })),
    actions: [],
  };
}

export function PlatformsProvider({ children }: { children: ReactNode }): JSX.Element {
  const { user } = useAuth();
  const client = useApiClient();
  const [status, setStatus] = useState<StatusMap>(INITIAL);
  const [nonce, setNonce] = useState(0);
  const roles = useMemo(() => user?.roles ?? [], [user]);
  const rolesKey = roles.join(',');

  const set = useCallback((id: PlatformId, s: PlatformStatus) => {
    setStatus((prev) => ({ ...prev, [id]: s }));
  }, []);

  // Retail
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    set('retail', { kind: 'loading' });
    void (async () => {
      try {
        const caps = await client.http.get<Capabilities>('/v3/retail/admin/capabilities');
        if (cancelled) return;
        // The API decides when it answers.
        set('retail', caps && caps.isAdmin ? { kind: 'ready', caps, source: 'api' } : { kind: 'not-admin' });
      } catch (e) {
        if (cancelled) return;
        // Route not deployed yet (404) or unreachable: keep today's behaviour, the ROP roles decide.
        if (ApiError.isApiError(e) && (e.httpStatus === 401 || e.httpStatus === 403)) set('retail', { kind: 'not-admin' });
        else if (hasRetailRole(roles)) set('retail', { kind: 'ready', caps: retailFromRoles(roles), source: 'roles' });
        else set('retail', { kind: 'not-admin' });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, user, rolesKey, nonce, set]);

  // Hub
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    set('hub', { kind: 'loading' });
    void (async () => {
      try {
        const caps = await hubApi.capabilities();
        if (cancelled) return;
        set('hub', caps.isAdmin ? { kind: 'ready', caps, source: 'api' } : { kind: 'not-admin' });
      } catch (e) {
        if (cancelled) return;
        if (e instanceof ConsentWaitingError) set('hub', { kind: 'consent-waiting' });
        else set('hub', { kind: 'error', message: e instanceof Error ? e.message : String(e) });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, nonce, set]);

  // Loans
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    set('loans', { kind: 'loading' });
    void (async () => {
      try {
        const caps = await loansApi.capabilities();
        if (cancelled) return;
        set('loans', caps.isAdmin ? { kind: 'ready', caps, source: 'api' } : { kind: 'not-admin' });
      } catch (e) {
        if (cancelled) return;
        if (e instanceof ConsentWaitingError) set('loans', { kind: 'consent-waiting' });
        else if (e instanceof LoansApiError && (e.status === 401 || e.status === 403)) set('loans', { kind: 'not-admin' });
        else set('loans', { kind: 'error', message: e instanceof Error ? e.message : String(e) });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, nonce, set]);

  // When the backend token turns up (consent granted, or a later silent retry works), look again.
  useEffect(
    () =>
      onBackendTokenState((s) => {
        if (s === 'available') setNonce((n) => n + 1);
      }),
    [],
  );

  const value = useMemo<PlatformsValue>(() => {
    const available = PLATFORMS.filter((pl) => {
      const st = status[pl.id];
      return st.kind === 'ready' && st.caps.isAdmin;
    });
    const loading = (Object.values(status) as PlatformStatus[]).some((s) => s.kind === 'loading');
    return { status, available, loading, refresh: () => setNonce((n) => n + 1) };
  }, [status]);

  return <PlatformsContext.Provider value={value}>{children}</PlatformsContext.Provider>;
}

export function usePlatforms(): PlatformsValue {
  const ctx = useContext(PlatformsContext);
  if (!ctx) throw new Error('usePlatforms must be used within PlatformsProvider');
  return ctx;
}
